"use client";

import React from "react";
import EditSection, { TextEditor } from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";
import { Input } from "rizzui";
import { PiUploadSimple } from "react-icons/pi";
import { getImageUrl } from "@/utils/image-url";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";

export default function UniversityAboutEdit({
  content,
  onContentChange,
}: SectionContentProps) {
  const updateContent = (key: string, value: any) => {
    onContentChange?.({ ...content, [key]: value });
  };

  return (
    <EditSection
      content={content}
      onContentChange={onContentChange || (() => {})}
      contentFields={
        <div className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Section Content</h4>
            <TextEditor
              label="Title"
              value={content.title}
              onChange={(v) => updateContent("title", v)}
              placeholder="About Our University Instacks"
            />
            <TextEditor
              label="Description"
              value={content.description}
              onChange={(v) => updateContent("description", v)}
              placeholder="At University Instacks, we believe..."
              multiline
            />
          </div>

          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">About Section Images</h4>
            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 block">
                Main Image (Left/Large)
              </label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-12 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0 relative group/img">
                  {content.imageUrl ? (
                    <img
                      src={getImageUrl(content.imageUrl) || content.imageUrl}
                      alt="Main Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      🖼️
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer">
                    <PiUploadSimple className="w-4 h-4 text-white" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error("Image must be under 5MB");
                          return;
                        }
                        const uploadPromise = homepageApi.uploadImage(file);
                        toast.promise(uploadPromise, {
                          loading: "Uploading main image...",
                          success: "Main image uploaded!",
                          error: "Failed to upload image",
                        });
                        const response = await uploadPromise;
                        if (response.success && response.data?.url) {
                          updateContent("imageUrl", response.data.url);
                        }
                      }}
                    />
                  </label>
                </div>
                <Input
                  value={content.imageUrl || ""}
                  onChange={(e) => updateContent("imageUrl", e.target.value)}
                  placeholder="Paste URL or upload image"
                  className="!rounded-lg text-xs flex-1 h-8"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 block">
                Secondary Overlay Image (Right/Overlapping)
              </label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-12 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0 relative group/img">
                  {content.imageUrl2 ? (
                    <img
                      src={getImageUrl(content.imageUrl2) || content.imageUrl2}
                      alt="Overlay Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      🖼️
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer">
                    <PiUploadSimple className="w-4 h-4 text-white" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error("Image must be under 5MB");
                          return;
                        }
                        const uploadPromise = homepageApi.uploadImage(file);
                        toast.promise(uploadPromise, {
                          loading: "Uploading overlay image...",
                          success: "Overlay image uploaded!",
                          error: "Failed to upload image",
                        });
                        const response = await uploadPromise;
                        if (response.success && response.data?.url) {
                          updateContent("imageUrl2", response.data.url);
                        }
                      }}
                    />
                  </label>
                </div>
                <Input
                  value={content.imageUrl2 || ""}
                  onChange={(e) => updateContent("imageUrl2", e.target.value)}
                  placeholder="Paste URL or upload image"
                  className="!rounded-lg text-xs flex-1 h-8"
                />
              </div>
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">University Details</h4>
            <div>
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                Established Text
              </label>
              <Input
                value={content.establishedText || ""}
                onChange={(e) => updateContent("establishedText", e.target.value)}
                placeholder="e.g., EST. 1971"
                className="!rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                Spinning Circle Text
              </label>
              <Input
                value={content.circleText || ""}
                onChange={(e) => updateContent("circleText", e.target.value)}
                placeholder="e.g., * Explore Future *Instacks University * Estd. 1971 "
                className="!rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                CTA Link Text
              </label>
              <Input
                value={content.ctaText || ""}
                onChange={(e) => updateContent("ctaText", e.target.value)}
                placeholder="e.g., University Overview"
                className="!rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                CTA Link URL
              </label>
              <Input
                value={content.ctaLink || ""}
                onChange={(e) => updateContent("ctaLink", e.target.value)}
                placeholder="e.g., #overview"
                className="!rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Statistics</h4>
            
            {/* Stat 1 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Stat 1 Value</label>
                <Input
                  value={content.stat1Value || ""}
                  onChange={(e) => updateContent("stat1Value", e.target.value)}
                  placeholder="e.g., 20,000"
                  className="!rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Stat 1 Label</label>
                <Input
                  value={content.stat1Label || ""}
                  onChange={(e) => updateContent("stat1Label", e.target.value)}
                  placeholder="e.g., Students"
                  className="!rounded-lg text-sm"
                />
              </div>
            </div>

            {/* Stat 2 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Stat 2 Value</label>
                <Input
                  value={content.stat2Value || ""}
                  onChange={(e) => updateContent("stat2Value", e.target.value)}
                  placeholder="e.g., 1,500"
                  className="!rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Stat 2 Label</label>
                <Input
                  value={content.stat2Label || ""}
                  onChange={(e) => updateContent("stat2Label", e.target.value)}
                  placeholder="e.g., Faculty & Staff"
                  className="!rounded-lg text-sm"
                />
              </div>
            </div>

            {/* Stat 3 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Stat 3 Value</label>
                <Input
                  value={content.stat3Value || ""}
                  onChange={(e) => updateContent("stat3Value", e.target.value)}
                  placeholder="e.g., 100+"
                  className="!rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Stat 3 Label</label>
                <Input
                  value={content.stat3Label || ""}
                  onChange={(e) => updateContent("stat3Label", e.target.value)}
                  placeholder="e.g., Programs"
                  className="!rounded-lg text-sm"
                />
              </div>
            </div>

            {/* Stat 4 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Stat 4 Value</label>
                <Input
                  value={content.stat4Value || ""}
                  onChange={(e) => updateContent("stat4Value", e.target.value)}
                  placeholder="e.g., 98%"
                  className="!rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Stat 4 Label</label>
                <Input
                  value={content.stat4Label || ""}
                  onChange={(e) => updateContent("stat4Label", e.target.value)}
                  placeholder="e.g., Alumni Employed"
                  className="!rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}
